/**
 * UnauthorizedBookingModal.jsx
 * Module: common
 *
 * The "you are not authorised to view this booking" popup shown on the
 * booking-history list after a booking-detail page bounced the user back.
 *
 * The air (pages/BookingDetailPage.jsx) and ocean (components/BookingHistory/
 * OceanCargoDetails.jsx) detail pages both fetch via single_booking_data, which
 * answers a queryId belonging to another account with a 404 "Booking not
 * found". Rather than each page rendering its own empty shell + error, they
 * navigate to /booking-history carrying UNAUTHORIZED_BOOKING_REDIRECT_STATE and
 * this component — mounted once on that list page — raises the message.
 */
import React from "react";
import { Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

/** Key set on react-router navigation state to request this popup. */
export const UNAUTHORIZED_BOOKING_STATE_KEY = "unauthorizedBooking";

/**
 * Navigation state a booking-detail page passes to the list route it redirects
 * to, e.g. navigate("/booking-history", { replace: true,
 * state: UNAUTHORIZED_BOOKING_REDIRECT_STATE }).
 */
export const UNAUTHORIZED_BOOKING_REDIRECT_STATE = {
  [UNAUTHORIZED_BOOKING_STATE_KEY]: true,
};

/**
 * The one wording used by every "you can't see this booking" popup, whichever
 * side triggers it — a customer opening a KAM link, a KAM opening a customer
 * link, or the backend 404 that this component reports. Exported (and imported
 * by layouts/PageLayout.jsx) so the two popups can't drift apart, and kept
 * role-neutral because the same sentence has to read correctly for both sides.
 */
export const UNAUTHORIZED_BOOKING_MESSAGE =
  "You are not authorised to view this booking detail.";

/**
 * @returns {JSX.Element} the popup, open only while the current history entry
 *   carries the unauthorized-booking flag
 */
const UnauthorizedBookingModal = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const open = Boolean(location.state?.[UNAUTHORIZED_BOOKING_STATE_KEY]);

  /**
   * OK / dismiss. Closing is done by stripping the flag off the current
   * history entry rather than by a local `open` state: the entry is replayed
   * whenever the user comes back to it via browser back/forward, and a flag
   * left behind would re-raise the popup on a list page the user reached
   * deliberately. Any other state the entry carried is preserved.
   * @returns {void}
   */
  const handleClose = () => {
    const { [UNAUTHORIZED_BOOKING_STATE_KEY]: _flag, ...rest } =
      location.state || {};
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(rest).length ? rest : null,
    });
  };

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      onCancel={handleClose}
      title={
        <span className="flex items-center gap-2">
          <ExclamationCircleFilled style={{ color: "#faad14" }} />
          Access Denied
        </span>
      }
      /* Explicit footer button instead of antd's okText: Tailwind's preflight
         (`@tailwind base` in index.css) resets `button { background-color:
         transparent }`, which flattens .ant-btn-primary to white-on-white and
         made the default OK button invisible. There's no ConfigProvider theme
         to fix that globally, so style it the way the rest of the app styles a
         primary action. */
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            autoFocus
            onClick={handleClose}
            className="px-6 py-2 text-sm font-semibold text-white bg-[#f1b21b] hover:bg-[#e0a518] rounded-lg transition-colors"
          >
            OK
          </button>
        </div>
      }
    >
      <p className="text-sm text-gray-700">{UNAUTHORIZED_BOOKING_MESSAGE}</p>
    </Modal>
  );
};

export default UnauthorizedBookingModal;
